import React, { useState, useEffect } from 'react';
import {
  FileText, Plus, Search, Download, Trash2, Play, Clock, Calendar,
  BarChart3, PieChart, TrendingUp, Filter, MoreVertical, RefreshCw
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import reportsApi from '@/api/reports';
import { FEATURES } from '@/lib/features';
import { FeatureUnavailable } from '@/components/data-display/FeatureUnavailable';

const typeConfig = {
  campaign_performance: { label: 'Campaign Performance', icon: TrendingUp, color: 'bg-blue-500' },
  lead_pipeline: { label: 'Lead Pipeline', icon: BarChart3, color: 'bg-emerald-500' },
  qr_analytics: { label: 'QR Analytics', icon: PieChart, color: 'bg-purple-500' },
  conversion_funnel: { label: 'Conversion Funnel', icon: TrendingUp, color: 'bg-amber-500' },
};

const statusColors = {
  pending: 'bg-amber-500',
  generating: 'bg-blue-500',
  completed: 'bg-emerald-500',
  failed: 'bg-red-500',
};

/**
 * Reports is switched off by default (lib/features.js): generated reports
 * read lifetime counters capped at 500 rows and scheduled reports are never
 * run. Set REACT_APP_FEATURE_REPORTS=true to show the screen.
 */
export default function Reports() {
  if (!FEATURES.reports) {
    return (
      <FeatureUnavailable
        title="Reports"
        reason="Generated and scheduled reports are being rebuilt so their figures match Analytics. Use the Analytics page for now."
      />
    );
  }
  return <ReportsScreen />;
}

export function ReportsScreen() {
  const [reports, setReports] = useState([]);
  const [summary, setSummary] = useState(null);
  const [scheduledReports, setScheduledReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showGenerateDialog, setShowGenerateDialog] = useState(false);
  const [showScheduleDialog, setShowScheduleDialog] = useState(false);
  const [activeTab, setActiveTab] = useState('generated');
  const [typeOptions, setTypeOptions] = useState([]);
  const [formatOptions, setFormatOptions] = useState([]);

  const [newReport, setNewReport] = useState({
    name: '',
    type: 'campaign_performance',
    format: 'json',
  });

  // Field names match CreateScheduledReportRequest (type, frequency,
  // email_recipients); the old report_type/schedule_type/recipients got a 422.
  const emptySchedule = { name: '', type: 'campaign_performance', frequency: 'weekly', email_recipients: '' };
  const [newSchedule, setNewSchedule] = useState(emptySchedule);

  useEffect(() => {
    loadReports();
    loadScheduledReports();
    loadOptions();
  }, []);

  const loadOptions = async () => {
    try {
      const [types, formats] = await Promise.all([
        reportsApi.getTypeOptions(),
        reportsApi.getFormatOptions(),
      ]);
      if (types.success) setTypeOptions(types.data);
      if (formats.success) setFormatOptions(formats.data);
    } catch (error) {
      console.error('Failed to load options:', error);
    }
  };

  const loadReports = async () => {
    setLoading(true);
    try {
      const [result, stats] = await Promise.all([
        reportsApi.list(),
        reportsApi.stats().catch(() => null),
      ]);
      if (result.success) {
        setReports(result.data);
      }
      setSummary(stats?.success ? stats.data : null);
    } catch (error) {
      toast.error('Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  const loadScheduledReports = async () => {
    try {
      const result = await reportsApi.listScheduled();
      if (result.success) {
        setScheduledReports(result.data);
      }
    } catch (error) {
      console.error('Failed to load scheduled reports:', error);
    }
  };

  const handleGenerate = async () => {
    if (!newReport.name.trim()) {
      toast.error('Report name is required');
      return;
    }

    try {
      const result = await reportsApi.generate(
        newReport.name,
        newReport.type,
        newReport.format
      );
      if (result.success) {
        toast.success('Report generation started!');
        setShowGenerateDialog(false);
        setNewReport({ name: '', type: 'campaign_performance', format: 'json' });
        loadReports();
      } else {
        toast.error(result.detail || 'Failed to generate report');
      }
    } catch (error) {
      toast.error('Failed to generate report');
    }
  };

  const handleScheduleCreate = async () => {
    if (!newSchedule.name.trim()) {
      toast.error('Schedule name is required');
      return;
    }

    try {
      const data = {
        ...newSchedule,
        email_recipients: newSchedule.email_recipients.split(',').map(e => e.trim()).filter(Boolean),
      };
      const result = await reportsApi.createScheduled(data);
      if (result.success) {
        toast.success('Scheduled report created!');
        setShowScheduleDialog(false);
        setNewSchedule(emptySchedule);
        loadScheduledReports();
      } else {
        toast.error(result.detail || 'Failed to create schedule');
      }
    } catch (error) {
      toast.error('Failed to create scheduled report');
    }
  };

  const handleDownload = async (report) => {
    try {
      await reportsApi.download(report.id, `${report.name}.${report.format || 'json'}`);
    } catch (error) {
      toast.error('Failed to download report');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this report?')) return;
    try {
      const result = await reportsApi.delete(id);
      if (result.success) {
        toast.success('Report deleted');
        loadReports();
      }
    } catch (error) {
      toast.error('Failed to delete report');
    }
  };

  const handleRunNow = async (id) => {
    try {
      const result = await reportsApi.runScheduledNow(id);
      if (result.success) {
        toast.success('Report generation triggered!');
        loadReports();
      }
    } catch (error) {
      toast.error('Failed to run report');
    }
  };

  const filteredReports = reports.filter(r =>
    r.name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="space-y-6" data-testid="reports-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Reports</h1>
          <p className="text-muted-foreground mt-1">Generate and schedule analytics reports</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowScheduleDialog(true)}>
            <Clock className="w-4 h-4 mr-2" />
            Schedule
          </Button>
          <Button onClick={() => setShowGenerateDialog(true)} data-testid="generate-report-btn">
            <Plus className="w-4 h-4 mr-2" />
            Generate Report
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Reports</p>
                <p className="text-2xl font-bold" data-testid="reports-total">{summary ? summary.total : '-'}</p>
              </div>
              <FileText className="w-8 h-8 text-primary/20" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Completed</p>
                <p className="text-2xl font-bold text-emerald-600">
                  {summary ? (summary.by_status?.completed || 0) : '-'}
                </p>
              </div>
              <BarChart3 className="w-8 h-8 text-emerald-500/20" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Generating</p>
                <p className="text-2xl font-bold text-blue-600">
                  {summary ? (summary.by_status?.generating || 0) + (summary.by_status?.pending || 0) : '-'}
                </p>
              </div>
              <RefreshCw className="w-8 h-8 text-blue-500/20" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Scheduled</p>
                <p className="text-2xl font-bold text-purple-600">{summary ? summary.scheduled : '-'}</p>
              </div>
              <Clock className="w-8 h-8 text-purple-500/20" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <div className="flex items-center justify-between">
          <TabsList>
            <TabsTrigger value="generated">Generated Reports</TabsTrigger>
            <TabsTrigger value="scheduled">Scheduled Reports</TabsTrigger>
          </TabsList>
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search reports..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 w-64"
              data-testid="search-reports"
            />
          </div>
        </div>

        {/* Generated Reports */}
        <TabsContent value="generated" className="mt-4">
          {loading ? (
            <div className="space-y-4">
              {[1, 2, 3].map(i => (
                <Card key={i} className="animate-pulse">
                  <CardContent className="p-4">
                    <div className="h-6 bg-muted rounded w-1/3 mb-2" />
                    <div className="h-4 bg-muted rounded w-1/4" />
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : filteredReports.length === 0 ? (
            <Card>
              <CardContent className="p-12 text-center">
                <FileText className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
                <h3 className="text-lg font-medium mb-2">No reports yet</h3>
                <p className="text-muted-foreground mb-4">Generate your first report to get started</p>
                <Button onClick={() => setShowGenerateDialog(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  Generate Report
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4" data-testid="reports-list">
              {filteredReports.map(report => {
                const config = typeConfig[report.type] || typeConfig.campaign_performance;
                const Icon = config.icon;
                return (
                  <Card key={report.id}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className={`w-10 h-10 rounded-lg ${config.color} flex items-center justify-center text-white`}>
                            <Icon className="w-5 h-5" />
                          </div>
                          <div>
                            <h3 className="font-medium">{report.name}</h3>
                            <div className="flex items-center gap-2 mt-1">
                              <Badge variant="outline">{report.type?.replace('_', ' ')}</Badge>
                              <Badge className={`${statusColors[report.status]} text-white text-xs`}>
                                {report.status}
                              </Badge>
                              <span className="text-xs text-muted-foreground">
                                {report.created_at ? new Date(report.created_at).toLocaleString() : ''}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {report.status === 'completed' && (
                            <Button variant="outline" size="sm" onClick={() => handleDownload(report)}>
                              <Download className="w-4 h-4 mr-1" />
                              Download
                            </Button>
                          )}
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon">
                                <MoreVertical className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => handleDelete(report.id)} className="text-destructive">
                                <Trash2 className="w-4 h-4 mr-2" /> Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* Scheduled Reports */}
        <TabsContent value="scheduled" className="mt-4">
          {scheduledReports.length === 0 ? (
            <Card>
              <CardContent className="p-12 text-center">
                <Clock className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
                <h3 className="text-lg font-medium mb-2">No scheduled reports</h3>
                <p className="text-muted-foreground mb-4">Set up automated report delivery</p>
                <Button onClick={() => setShowScheduleDialog(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  Create Schedule
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4" data-testid="scheduled-list">
              {scheduledReports.map(schedule => (
                <Card key={schedule.id}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-lg bg-purple-500 flex items-center justify-center text-white">
                          <Clock className="w-5 h-5" />
                        </div>
                        <div>
                          <h3 className="font-medium">{schedule.name}</h3>
                          <div className="flex items-center gap-2 mt-1">
                            <Badge variant="outline">{schedule.frequency}</Badge>
                            <span className="text-xs text-muted-foreground">
                              Next: {schedule.next_run_at ? new Date(schedule.next_run_at).toLocaleString() : 'Not scheduled'}
                            </span>
                          </div>
                        </div>
                      </div>
                      <Button variant="outline" size="sm" onClick={() => handleRunNow(schedule.id)}>
                        <Play className="w-4 h-4 mr-1" />
                        Run Now
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Generate Dialog */}
      <Dialog open={showGenerateDialog} onOpenChange={setShowGenerateDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Generate Report</DialogTitle>
            <DialogDescription>Create a new analytics report</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="name">Report Name *</Label>
              <Input
                id="name"
                placeholder="Monthly Campaign Performance"
                value={newReport.name}
                onChange={(e) => setNewReport({ ...newReport, name: e.target.value })}
                data-testid="report-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="type">Report Type</Label>
              <Select
                value={newReport.type}
                onValueChange={(value) => setNewReport({ ...newReport, type: value })}
              >
                <SelectTrigger data-testid="report-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {typeOptions.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="format">Format</Label>
              <Select
                value={newReport.format}
                onValueChange={(value) => setNewReport({ ...newReport, format: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {formatOptions.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowGenerateDialog(false)}>Cancel</Button>
            <Button onClick={handleGenerate} data-testid="generate-report-submit">
              Generate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Schedule Dialog */}
      <Dialog open={showScheduleDialog} onOpenChange={setShowScheduleDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Schedule Report</DialogTitle>
            <DialogDescription>Set up automated report delivery</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="schedule-name">Schedule Name *</Label>
              <Input
                id="schedule-name"
                placeholder="Weekly Performance Report"
                value={newSchedule.name}
                onChange={(e) => setNewSchedule({ ...newSchedule, name: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Report Type</Label>
              <Select
                value={newSchedule.type}
                onValueChange={(value) => setNewSchedule({ ...newSchedule, type: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {typeOptions.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Frequency</Label>
              <Select
                value={newSchedule.frequency}
                onValueChange={(value) => setNewSchedule({ ...newSchedule, frequency: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="daily">Daily</SelectItem>
                  <SelectItem value="weekly">Weekly</SelectItem>
                  <SelectItem value="monthly">Monthly</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="recipients">Recipients (comma-separated emails)</Label>
              <Input
                id="recipients"
                placeholder="team@company.com, manager@company.com"
                value={newSchedule.email_recipients}
                onChange={(e) => setNewSchedule({ ...newSchedule, email_recipients: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowScheduleDialog(false)}>Cancel</Button>
            <Button onClick={handleScheduleCreate}>
              Create Schedule
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
